import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    TextField,
    IconButton,
    Avatar,
    useTheme,
    alpha,
    keyframes // <-- IMPORT KEYFRAMES
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonIcon from '@mui/icons-material/Person';
import axios from 'axios';

// IMPORT YOUR ID ENCODER UTILITY
import { encodeId } from '../utils/idEncoder';

const API_BASE = 'https://apistaging.workhorse.com.sg/ai-api';

// --- CREATE THE BOUNCING ANIMATION FOR TYPING DOTS ---
const bounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-4px); }
`;

interface Message {
    role: 'system' | 'ai' | 'user';
    content: string;
}

const AiChatSection = () => {
    const theme = useTheme();
    const chatEndRef = useRef<HTMLDivElement>(null);

    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState<Message[]>([
        {
            role: 'ai',
            content: 'Hello! I am your WorkHorse AI Assistant. How can I help you find the right commercial vehicle today?'
        }
    ]);

    // Auto-scroll to bottom of chat
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    const handleSend = async () => {
        if (!input.trim()) return;

        const userMsg = input.trim();
        setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
        setInput('');
        setLoading(true);

        try {
            const response = await axios.post<{ reply: string, vehicle_ids: string[] }>(`${API_BASE}/chat`, {
                query: userMsg,
                history: messages.map(m => ({ role: m.role === 'ai' ? 'assistant' : m.role, content: m.content }))
            });

            setMessages(prev => [...prev, {
                role: 'ai',
                content: response.data.reply
            }]);

        } catch (error) {
            setMessages(prev => [...prev, { role: 'ai', content: '⚠️ Sorry, I am having trouble connecting to the server right now.' }]);
        } finally {
            setLoading(false);
        }
    };

    // --- CUSTOM MESSAGE FORMATTER ---
    const formatMessage = (text: string) => {
        if (!text) return "";
        let formattedText = text;

        // 1. Convert newlines to <br/>
        formattedText = formattedText.replace(/\n/g, '<br/>');

        // 2. Convert bold text
        formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');

        // 3. INTERCEPT & ENCRYPT VEHICLE LINKS
        formattedText = formattedText.replace(
            /\[(.*?)\]\((?:\/vehicle-details\/)?(\d+)\)/g,
            (match, linkText, rawId) => {
                const encryptedId = encodeId(Number(rawId));
                return `<a href="/vehicle-details/${encryptedId}" target="_blank" style="display:inline-block; margin-top:6px; margin-bottom:12px; padding:6px 14px; background-color:#fff; color:#FF8E00; border:1px solid #FF8E00; border-radius:6px; font-weight:bold; text-decoration:none; transition:0.2s;">${linkText} ↗</a>`;
            }
        );

        // 4. Fallback for any other general links
        formattedText = formattedText.replace(
            /\[(.*?)\]\((.*?)\)/g,
            '<a href="$2" target="_blank" style="color:#FF8E00; font-weight:bold; text-decoration:none;">$1 ↗</a>'
        );

        return formattedText;
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: theme.palette.mode === 'light' ? '#F9FAFB' : alpha(theme.palette.background.paper, 0.5),
                p: { xs: 2, md: 4 }
            }}
        >
            {/* Header Text */}
            <Box textAlign="center" mb={{ xs: 2, md: 3 }}>
                <Typography variant="overline" sx={{ fontWeight: 'bold', letterSpacing: 2, color: '#FF8E00' }}>
                    SMART ASSISTANT
                </Typography>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Need Help Finding a Vehicle?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
                    Ask our AI about current market trends, specific vehicle specifications, or let it match you with the perfect commercial vehicle.
                </Typography>
            </Box>

            {/* Chat Interface Paper */}
            <Paper
                elevation={0}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    width: '100%',
                    maxWidth: '800px',
                    mx: 'auto',
                    minHeight: '450px',
                    maxHeight: '800px',
                    borderRadius: 4,
                    overflow: 'hidden',
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)'
                }}
            >
                {/* Chat Header Gradient */}
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #FFA100 0%, #FF8E00 100%)',
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        color: 'white',
                        flexShrink: 0
                    }}
                >
                    <AutoAwesomeIcon />
                    <Typography variant="h6" fontWeight="bold">
                        WorkHorse AI
                    </Typography>
                </Box>

                {/* Chat Messages Area */}
                <Box
                    sx={{
                        flexGrow: 1,
                        overflowY: 'auto',
                        p: { xs: 2, md: 3 },
                        bgcolor: theme.palette.background.paper
                    }}
                >
                    <Stack spacing={3}>
                        {messages.map((msg, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                                    gap: 2,
                                    alignItems: 'flex-start'
                                }}
                            >
                                <Avatar
                                    sx={{
                                        bgcolor: msg.role === 'user' ? '#1e293b' : '#fff',
                                        color: msg.role === 'user' ? '#fff' : '#FF8E00',
                                        border: msg.role === 'ai' ? '1px solid #FF8E00' : 'none',
                                        width: 35, height: 35
                                    }}
                                >
                                    {msg.role === 'user' ? <PersonIcon fontSize="small" /> : <AutoAwesomeIcon fontSize="small" />}
                                </Avatar>

                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        maxWidth: { xs: '90%', md: '80%' },
                                        borderRadius: 3,
                                        borderTopLeftRadius: msg.role === 'ai' ? 4 : 24,
                                        borderTopRightRadius: msg.role === 'user' ? 4 : 24,
                                        bgcolor: msg.role === 'user' ? '#f8fafc' : alpha('#FF8E00', 0.05),
                                        border: msg.role === 'user' ? `1px solid ${theme.palette.divider}` : '1px solid transparent',
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ lineHeight: 1.6, fontSize: '0.95rem' }}
                                        dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                                    />
                                </Paper>
                            </Box>
                        ))}

                        {/* NEW: Animated Typing Indicator */}
                        {loading && (
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <Avatar sx={{ bgcolor: '#fff', color: '#FF8E00', border: '1px solid #FF8E00', width: 35, height: 35 }}>
                                    <AutoAwesomeIcon fontSize="small" />
                                </Avatar>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        px: 2,
                                        py: 2.5,
                                        borderRadius: 3,
                                        borderTopLeftRadius: 4,
                                        bgcolor: alpha('#FF8E00', 0.05),
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.8,
                                    }}
                                >
                                    <Box sx={{ width: 6, height: 6, bgcolor: '#FF8E00', borderRadius: '50%', animation: `${bounce} 1.4s infinite ease-in-out both`, animationDelay: '-0.32s' }} />
                                    <Box sx={{ width: 6, height: 6, bgcolor: '#FF8E00', borderRadius: '50%', animation: `${bounce} 1.4s infinite ease-in-out both`, animationDelay: '-0.16s' }} />
                                    <Box sx={{ width: 6, height: 6, bgcolor: '#FF8E00', borderRadius: '50%', animation: `${bounce} 1.4s infinite ease-in-out both` }} />
                                </Paper>
                            </Box>
                        )}
                        <div ref={chatEndRef} />
                    </Stack>
                </Box>

                {/* Chat Input Area */}
                <Box
                    sx={{
                        p: 2,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        bgcolor: theme.palette.background.paper,
                        flexShrink: 0
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                            fullWidth
                            placeholder="Type your message here..."
                            variant="outlined"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '50px',
                                    bgcolor: theme.palette.mode === 'light' ? '#f8fafc' : 'transparent',
                                }
                            }}
                        />
                        <IconButton
                            onClick={handleSend}
                            disabled={loading || !input.trim()}
                            sx={{
                                background: 'linear-gradient(135deg, #FFA100 0%, #FF8E00 100%)',
                                color: 'white',
                                width: 48,
                                height: 48,
                                '&:hover': { background: 'linear-gradient(135deg, #FF8E00 0%, #FFA100 100%)' },
                                '&.Mui-disabled': { background: '#e2e8f0', color: '#94a3b8' }
                            }}
                        >
                            <SendIcon />
                        </IconButton>
                    </Stack>
                </Box>
            </Paper>
        </Box>
    );
};

export default AiChatSection;